import { Save, Trash2 } from "lucide-react";
import { Control } from "react-hook-form";
import { Button } from "../../../../../../components/ui/common/button";
import { TableCell } from "../../../../../../components/ui/common/table";
import { FormInput } from "../../../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../../components/ui/Input/FormSelector/FormSelect";
import { MEASUREMENT_TYPES } from "../../../../../../config/enums/MeasurementTypes";
import { validateCultivationNumber } from "../../../../../../utils/utils";
import { PACKAGING_NAME_MAX_LENGTH } from "../packagingLevel.config";
import PackagingMaterialWeightInput from "./PackagingMaterialWeightInput";

const numberValidation = {
  validate: {
    valueCheck: (v: any) => validateCultivationNumber(v)
  }
};

const cellClass = "tw-px-4 tw-py-3 tw-align-middle";
const tableFieldClass =
  "[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0 [&_input]:tw-h-9 [&_input]:tw-text-xs [&_button]:!tw-h-9 [&_button]:tw-text-xs [&_p]:tw-text-[10px] [&_p]:tw-leading-tight [&_p]:tw-mt-1";

export interface PackagingMaterialFormCellsProps {
  control: Control<any>;
  packagingTypeData: any[];
  onMaterialTypeChange: (value: number) => void;
  materialWeightUnit: MEASUREMENT_TYPES;
  onMaterialWeightUnitChange: (value: MEASUREMENT_TYPES) => void;
  lockStatus: boolean;
  canSave: boolean;
  onSave: () => void;
  onClear: () => void;
}

const PackagingMaterialFormCells = ({
  control,
  packagingTypeData,
  onMaterialTypeChange,
  materialWeightUnit,
  onMaterialWeightUnitChange,
  lockStatus,
  canSave,
  onSave,
  onClear
}: PackagingMaterialFormCellsProps) => (
  <>
    <TableCell className={cellClass}>
      <div className={tableFieldClass}>
        <FormInput
          control={control}
          name="packageName"
          label=""
          placeholder="e.g. Bottle Cap"
          disabled={lockStatus}
          autoComplete="off"
          rules={{
            required: "Packaging name is required",
            validate: (value: string) =>
              value?.length <= PACKAGING_NAME_MAX_LENGTH ||
              `Maximum ${PACKAGING_NAME_MAX_LENGTH} characters allowed`
          }}
        />
      </div>
    </TableCell>
    <TableCell className={cellClass}>
      <div className={tableFieldClass}>
        <FormSelect
          control={control}
          name="packageMaterial"
          label=""
          placeholder="Select"
          options={packagingTypeData}
          disabled={lockStatus}
          required
          errorMessage="Packaging material is required"
          className="!tw-h-9 tw-text-xs"
          onChange={value => onMaterialTypeChange(Number(value))}
        />
      </div>
    </TableCell>
    <TableCell className={cellClass}>
      <PackagingMaterialWeightInput
        control={control}
        name="materialWeight"
        weightUnit={materialWeightUnit}
        onUnitChange={onMaterialWeightUnitChange}
        disabled={lockStatus}
        placeholder="0"
        required
        errorMessage="Material Weight is required"
        className={tableFieldClass}
      />
    </TableCell>
    <TableCell className={cellClass}>
      <div className={tableFieldClass}>
        <FormInputNumber
          control={control}
          name="materialCost"
          label=""
          placeholder="0"
          disabled={lockStatus}
          enableLimitedDecimal
          rules={numberValidation}
        />
      </div>
    </TableCell>
    <TableCell className={cellClass}>
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
        <Button
          type="button"
          size="sm"
          className="tw-h-8 tw-w-8 tw-p-0 tw-shrink-0"
          disabled={!canSave}
          onClick={onSave}
        >
          <Save className="tw-h-3.5 tw-w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="tw-h-8 tw-w-8 tw-p-0 tw-shrink-0 tw-text-destructive hover:tw-text-destructive"
          onClick={onClear}
        >
          <Trash2 className="tw-h-3.5 tw-w-3.5" />
        </Button>
      </div>
    </TableCell>
  </>
);

export default PackagingMaterialFormCells;
